import { useState } from 'react';
import { useLoaderData } from 'react-router';

import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { TeamGrid, TeamMemberOverlay } from '~/ui-rocco/pages/detail-shared';
import type { TeamMember } from '~/ui-rocco/pages/detail-shared';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { TeamMemberSlide } from './TeamMemberSlide';
import {
    ABOUT_SECTION_CAP_CLASS,
    ABOUT_SECTION_CLASS,
    ABOUT_SECTION_HEADING_CLASS,
} from './about-section-heading';
import { TeamMemberDialog } from './TeamMemberDialog';
import {
    useBookMeetingGateEnabled,
    useOpenBookMeetingGate,
} from '~/modules-rocco/booking/book-meeting-gate-state';

/**
 * Meet the Team, as a section of its own under the About tab's accordion —
 * the portrait grid with the legacy team-member modal (portrait, role, gated
 * contact links, rich-text bio) on tile click; on the phone the overview's
 * tiles and overlay, in the grid. Headed in the overview's own voice, the
 * "Who we are" line, rather than numbered as a row.
 */
export const ProfessionalAboutTeamSection = () => {
    const { teamMembers, professionalOverview } =
        useLoaderData<typeof loader>();
    const [selectedId, setSelectedId] = useState<number | null>(null);
    // The phone's tiles open the overview's overlay rather than the dialog.
    const [openMember, setOpenMember] = useState<TeamMember | null>(null);
    // The branch list lives on the page's initial data, not the overview's.
    const initial = useProfessionalInitialRouteData();
    const members = teamMembers;
    const selected = members.find((member) => member.id === selectedId);
    const bookMeetingEnabled = useBookMeetingGateEnabled();
    const openBookMeetingGate = useOpenBookMeetingGate();

    // The same offer the profile's Team carousel makes, on the same people.
    // Without it the tab a reader opens to read about the practice was the one
    // place their portraits led nowhere but a bio.
    const onBookMeeting = bookMeetingEnabled
        ? (member: TeamMember) =>
              openBookMeetingGate({
                  professionalUrlSegment: professionalOverview.URLSegment,
                  siteTreeId: professionalOverview.ID,
                  preferredTeamMemberId: member.id,
                  preferredTeamMemberName: member.name,
                  paneImage: member.image || undefined,
                  expectsTeamPicker: members.length > 0,
                  professionalName: professionalOverview.Title,
                  companyLogo: professionalOverview.CompanyLogo,
                  logoBackgroundColor: professionalOverview.LogoBackgroundColor,
              })
        : undefined;
    if (members.length === 0) return null;

    return (
        <section className={ABOUT_SECTION_CLASS}>
            <div className={ABOUT_SECTION_CAP_CLASS}>
                <h2 className={ABOUT_SECTION_HEADING_CLASS}>Meet the Team</h2>
                <div className="max-md:pt-3 md:pt-[3rem]">
                    {/* Below md: the overview's tiles — the booking on the
                        portrait, the person behind the plus — in the grid's
                        two columns, opening the overview's overlay. */}
                    <div className="grid grid-cols-2 gap-x-2 gap-y-8 md:hidden">
                        {members.map((member) => (
                            <TeamMemberSlide
                                key={member.id}
                                member={member}
                                onBookMeeting={onBookMeeting}
                                onOpen={setOpenMember}
                            />
                        ))}
                    </div>
                    <div className="max-md:hidden">
                        <TeamGrid
                            members={members}
                            className="md:gap-y-[6.25rem]"
                            onMemberClick={(member) => setSelectedId(member.id)}
                            onBookMeeting={onBookMeeting}
                        />
                    </div>
                    {openMember ? (
                        <TeamMemberOverlay
                            displayName={
                                openMember.name || professionalOverview.Title
                            }
                            isPerson={Boolean(openMember.name)}
                            jobTitle={openMember.jobTitle}
                            summary={openMember.summary}
                            thumbnail={openMember.image}
                            practice={{
                                id: professionalOverview.ID,
                                name: professionalOverview.Title,
                                link: professionalOverview.Link,
                                companyLogo:
                                    professionalOverview.CompanyLogo ?? '',
                                logoBackgroundColor:
                                    professionalOverview.LogoBackgroundColor ??
                                    '',
                                customEnquiryMessage:
                                    professionalOverview.CustomEnquiryMessage ??
                                    undefined,
                                enquiryMessageOverrideEnabled:
                                    professionalOverview.EnquiryMessageOverrideEnabled ??
                                    undefined,
                                enquiryMessagePlaceholder:
                                    professionalOverview.EnquiryMessagePlaceholder ??
                                    undefined,
                                branchOptions: initial?.branchOptions,
                            }}
                            subject={{
                                itemType: 'professional',
                                siteTreeID: professionalOverview.ID,
                            }}
                            onBookMeeting={
                                onBookMeeting
                                    ? () => onBookMeeting(openMember)
                                    : undefined
                            }
                            onClose={() => setOpenMember(null)}
                        />
                    ) : null}
                    {selected ? (
                        <TeamMemberDialog
                            member={{
                                id: selected.id,
                                name: selected.name,
                                jobTitle: selected.jobTitle,
                                avatarSrc: selected.image,
                                detailsPageImageSrc: selected.image,
                                phone: selected.phone,
                                email: selected.email,
                                summary: selected.summary,
                                website: selected.website,
                                linkedInLink: selected.linkedInLink,
                                hideJobTitle: selected.hideJobTitle,
                                hideEmail: selected.hideEmail,
                                hidePhone: selected.hidePhone,
                                hideLinkedInLink: selected.hideLinkedInLink,
                            }}
                            website={selected.website ?? ''}
                            professionalId={professionalOverview.ID}
                            onClose={() => setSelectedId(null)}
                        />
                    ) : null}
                </div>
            </div>
        </section>
    );
};
